/**
 * REL-13 — who owns the Today hero's coach line.
 *
 * ## ⚖️ THE RULING (founder, 2026-08-16)
 *
 * **On a run day the post-run note WINS; the draft-week chore defers.** The
 * chore's own window runs to Friday, so deferring it costs nothing. Losing the
 * note costs the athlete the one message that was about the run they just
 * finished — and costs the product a push notification that lands on a page
 * where the promised note is not there.
 *
 * ## WHY THIS IS A MODULE AND NOT A CONJUNCT IN `today.tsx`
 *
 * It was a conjunct: `!proactiveType && coachReactionToday?.text`. That single
 * `!` yielded to ALL EIGHT trigger kinds, including the three chores that rank
 * LAST in `PROACTIVE_PRIORITY` — so the gate treated a PR celebration and a
 * "time to draft week 4" nag as equally entitled to silence a post-run note.
 *
 * `reaction-echo-push-coherence-memo.md` §2 traced what that costs: the push
 * for the note is enqueued by the server, which knows nothing about what Today
 * will render. Tapping the banner lands on a page whose coach line has been
 * replaced by the ordinary motivation line AND whose href has degraded from
 * `/v2/chat?...&run=<id>` to a bare `/v2/chat`. No `run=` means no activation
 * nonce, which means chat mounts with no idea which run the athlete is asking
 * about. §2.4: the two gates are anti-correlated — the dispatcher cancels the
 * push on a view event, and a suppressed echo can never be viewed, so the
 * mechanism that empties the destination is the same one that forces the send.
 *
 * The memo's §2.5 records that nothing tested this gate. That was still true on
 * 2026-08-16: a repo-wide search for `reactionEcho` found one hit, in a
 * component prop test. A pure resolver is the cheapest way to make the decision
 * addressable at all — the inline form could only ever be source-pinned, and
 * a source pin cannot enumerate eight trigger kinds.
 *
 * ⚠️ AND IT SIDESTEPS THE MEMO'S §0 TRAP. Testing this through the detectors
 * would require a Thursday-or-later fixture clock (`detectDraftNextWeek`
 * returns null before Monday-based dow 3) and a `lastFired` value that does not
 * accidentally debounce the candidate away. Both are easy to get wrong in a way
 * that leaves the test exercising nothing while passing — which is exactly the
 * error that produced a published-then-retracted root cause. This function
 * takes the trigger type directly, so there is no clock to get wrong.
 */

import { isChoreTrigger, type ProactiveTriggerType } from './proactiveCopy';
import type { CoachReaction } from './run/coachReaction';

export interface HeroCoachLineInput {
  /** `useProactiveChecks().primaryHint?.type` — the highest-priority trigger
   *  DETECTED this render (not necessarily one that fired a notification). */
  proactiveType: ProactiveTriggerType | null;
  /** `useCoachReactionToday()` — the server-frozen post-run note, or null. */
  reaction: CoachReaction | null | undefined;
  /** `useMotivation().data?.message` — the ordinary daily line. */
  motivationLine: string | null | undefined;
}

export interface HeroCoachLineResolution {
  /** The reaction when it won the slot; null when it did not. */
  echo: CoachReaction | null;
  /** What the hero renders. */
  line: string | null;
  /** Where a tap goes. Carries `?run=` if and only if the echo won — which is
   *  what lets `useReactionTapActivation` mint the run-context nonce. */
  href: string;
}

/**
 * Resolve the hero's coach line, href, and whether the echo won its slot.
 *
 * Precedence, highest first:
 *   1. A CELEBRATION or SAFETY trigger — `pr_celebration`, `streak_milestone`,
 *      `race_countdown`, `comeback`, `compliance_drop`. These still beat the
 *      echo, unchanged. They are time-bound and about something other than the
 *      run; a PR is not something to sit on until tomorrow.
 *   2. The POST-RUN NOTE, when the server froze one.
 *   3. A CHORE — `season_wrap`, `draft_next_week`, `unlogged_sessions`. These
 *      now defer to the note. Each has a multi-day window.
 *   4. The ordinary daily motivation line.
 *
 * ⚠️ "RUN DAY" IS DEFINED AS "THE SERVER FROZE A NOTE", and that is a product
 * decision worth stating rather than assuming. The server's echo window is
 * TODAY **or YESTERDAY** in the athlete's local zone
 * (`api/index.js` handleCoachReactionToday), and `local_run_date` is filtered
 * server-side then dropped from the DTO — the client is never told which of the
 * two it was. So a note from yesterday's evening run also outranks a chore this
 * morning. That is the intended reading of the ruling ("the note wins over the
 * chore"), and the alternative — widening the wire contract with a run date
 * purely to narrow this gate — would trade a real disclosure for a distinction
 * the athlete does not experience.
 */
export function resolveHeroCoachLine(input: HeroCoachLineInput): HeroCoachLineResolution {
  const { proactiveType, reaction, motivationLine } = input;

  // A trigger outranks the echo only if it is NOT a chore. `isChoreTrigger`
  // narrows nulls itself, so a missing trigger cannot outrank anything.
  const outranked = !!proactiveType && !isChoreTrigger(proactiveType);

  // Fail-closed, unchanged from the original gate: only when the server
  // actually returned a text. The client never fabricates a reaction (§12).
  const echo = !outranked && reaction?.text ? reaction : null;

  return {
    echo,
    line: echo ? echo.text : (motivationLine ?? null),
    href: echo?.href ?? '/v2/chat',
  };
}
